import { OpenFeatureEventEmitter, ProviderEvents, ProviderNotReadyError, FlagNotFoundError, TypeMismatchError, ParseError } from '@openfeature/web-sdk';
import { transformContext, toResolutionDetails, isType } from '@openfeature/config-cat-core';
import { getClient, PollingMode } from 'configcat-js-ssr';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class ConfigCatWebProvider {
    constructor(clientFactory) {
        this.events = new OpenFeatureEventEmitter();
        this._hasError = false;
        this.runsOn = 'client';
        this.metadata = {
            name: ConfigCatWebProvider.name,
        };
        this._clientFactory = clientFactory;
    }
    static create(sdkKey, options) {
        // Let's create a shallow copy to not mess up caller's options object.
        options = options ? Object.assign({}, options) : {};
        return new ConfigCatWebProvider((provider) => {
            const oldSetupHooks = options === null || options === void 0 ? void 0 : options.setupHooks;
            options.setupHooks = (hooks) => {
                oldSetupHooks === null || oldSetupHooks === void 0 ? void 0 : oldSetupHooks(hooks);
                hooks.on('configChanged', (projectConfig) => provider.events.emit(ProviderEvents.ConfigurationChanged, {
                    flagsChanged: projectConfig ? Object.keys(projectConfig.settings) : undefined,
                }));
                hooks.on('clientError', (message, error) => {
                    provider._hasError = true;
                    provider.events.emit(ProviderEvents.Error, {
                        message: message,
                        metadata: error,
                    });
                });
            };
            return getClient(sdkKey, PollingMode.AutoPoll, options);
        });
    }
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this._clientFactory(this);
            yield client.waitForReady();
            this._client = client;
        });
    }
    get configCatClient() {
        return this._client;
    }
    onClose() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            (_a = this._client) === null || _a === void 0 ? void 0 : _a.dispose();
        });
    }
    resolveBooleanEvaluation(flagKey, defaultValue, context) {
        return this.evaluate(flagKey, 'boolean', context);
    }
    resolveStringEvaluation(flagKey, defaultValue, context) {
        return this.evaluate(flagKey, 'string', context);
    }
    resolveNumberEvaluation(flagKey, defaultValue, context) {
        return this.evaluate(flagKey, 'number', context);
    }
    resolveObjectEvaluation(flagKey, defaultValue, context) {
        const objectValue = this.evaluate(flagKey, 'object', context);
        return objectValue;
    }
    evaluate(flagKey, flagType, context) {
        if (!this._client) {
            throw new ProviderNotReadyError('Provider is not initialized');
        }
        const _a = this._client
            .snapshot()
            .getValueDetails(flagKey, undefined, transformContext(context)), { value } = _a, evaluationData = __rest(_a, ["value"]);
        if (this._hasError && !evaluationData.errorMessage && !evaluationData.errorException) {
            this._hasError = false;
            this.events.emit(ProviderEvents.Ready);
        }
        if (typeof value === 'undefined') {
            throw new FlagNotFoundError();
        }
        if (flagType !== 'object') {
            return toResolutionDetails(flagType, value, evaluationData);
        }
        if (!isType('string', value)) {
            throw new TypeMismatchError();
        }
        let json;
        try {
            json = JSON.parse(value);
        }
        catch (e) {
            throw new ParseError(`Unable to parse "${value}" as JSON`);
        }
        return toResolutionDetails(flagType, json, evaluationData);
    }
}

export { ConfigCatWebProvider };
